/**
 * useWebUpdateMonitor — browser/SW-driven update detection.
 *
 * Electron has electron-updater + DesktopUpdateBanner. The plain browser
 * web target had no equivalent — once a tab loaded a build it could stay
 * on that build indefinitely, with the user needing to hard-refresh (or
 * worse, unregister the service worker) to pick up a new deploy. This
 * hook closes that gap.
 *
 * How it works:
 *   1. We register a single service worker in mobile/web/index.html
 *      (`/service-worker.js`, `updateViaCache: 'none'`). The SW itself
 *      doesn't intercept fetches — its only job is web push — but the
 *      browser only fires `updatefound` lifecycle events on a SW that
 *      it's actively monitoring. So the SW doubles as our update-pulse.
 *   2. This hook grabs the registration, listens for `updatefound`, then
 *      watches the new worker's `statechange`. When it reaches
 *      `installed`, `activating` or `activated` (the SW uses skipWaiting, so
 *      it may skip `installed`) AND a controller existed when `updatefound`
 *      fired (an update, not a first install), we flip `updateAvailable = true`. The banner
 *      surfaces a "Reload to apply" prompt.
 *   3. We also call `registration.update()` on every visibilitychange
 *      → visible transition (so returning to the tab forces an update
 *      check) and on a 30-minute interval (so long-running open tabs
 *      eventually catch new builds without a foreground roundtrip).
 *
 * No-op outside web: Platform.OS !== 'web' short-circuits at every entry
 * point. Electron (which is `isWeb && isElectron`) is also short-circuited
 * — electron-updater + DesktopUpdateBanner handle that surface; this hook
 * would double-toast otherwise.
 */

import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { isElectron } from './platform';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

interface WebUpdateMonitor {
  updateAvailable: boolean;
  reload: () => void;
  dismiss: () => void;
}

export function useWebUpdateMonitor(): WebUpdateMonitor {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const reload = useCallback(() => {
    try { window.location.reload(); } catch { /* noop */ }
  }, []);

  const dismiss = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || isElectron) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    let activeReg: ServiceWorkerRegistration | null = null;
    const newWorkerStatechangeHandlers = new WeakMap<ServiceWorker, () => void>();

    // The SW calls skipWaiting() + clients.claim(), so a new worker races
    // through installed -> activating -> activated and takes over the
    // controller. Whether this is an update (vs. the origin's first install)
    // must therefore be read when `updatefound` fires, before the claim, and
    // any of those three states counts as "update available".
    const hasController = () => !!navigator.serviceWorker.controller;
    const UPDATE_STATES = ['installed', 'activating', 'activated'];

    const onUpdateFound = (reg: ServiceWorkerRegistration) => () => {
      const installing = reg.installing;
      if (!installing) return;
      const isUpdate = hasController();
      let surfaced = false;
      const handler = () => {
        if (cancelled || surfaced || !isUpdate) return;
        if (UPDATE_STATES.includes(installing.state)) {
          surfaced = true;
          setUpdateAvailable(true);
        }
      };
      newWorkerStatechangeHandlers.set(installing, handler);
      installing.addEventListener('statechange', handler);
    };

    const attach = (reg: ServiceWorkerRegistration) => {
      if (cancelled) return;
      activeReg = reg;
      // If there's already a waiting worker by the time we attach (we
      // missed the live updatefound because React mounted late), surface
      // immediately.
      if (reg.waiting && hasController()) {
        setUpdateAvailable(true);
      }
      reg.addEventListener('updatefound', onUpdateFound(reg));
    };

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) {
        attach(reg);
      } else {
        // Registration hasn't completed yet (index.html does it on
        // window.load — React may have mounted slightly earlier). Wait
        // for it via the `ready` promise, which resolves to the active
        // registration once the SW is installed and active.
        navigator.serviceWorker.ready.then(attach).catch(() => { /* noop */ });
      }
    }).catch(() => { /* noop */ });

    // Force an update check whenever the tab regains visibility. Sites
    // left open across deploys would otherwise never re-check until
    // a full navigation or the browser's stock 24h tick.
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      activeReg?.update().catch(() => { /* noop */ });
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Long-poll for very-long-running tabs that never lose visibility
    // (background-music players, dashboards, etc.).
    interval = setInterval(() => {
      activeReg?.update().catch(() => { /* noop */ });
    }, UPDATE_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (interval) clearInterval(interval);
    };
  }, []);

  return { updateAvailable, reload, dismiss };
}
